/**
 * Module Permissions Configuration
 * Defines all available modules and their metadata
 */

export const MODULE_PERMISSIONS = {
  CONTACTS: 'contacts',
  DONATION_PLEDGE: 'donation_pledge',
  DONATION_REMINDER: 'donation_reminder',
  DONATION_TRANSACTION: 'donation_transaction',
  EMAIL_MARKETING: 'email_marketing',
  WHATSAPP_MARKETING: 'whatsapp_marketing',
  DONATION_SCHEME: 'donation_scheme',
  TAGS: 'tags',
  EMAIL_TEMPLATE: 'email_template',
  WHATSAPP_TEMPLATE: 'whatsapp_template',
  TRUST_MASTER: 'trust_master',
};

/**
 * Permission relationships for RBAC
 * Defines which parent permissions allow read-only access to sub-features
 */
export const PERMISSION_RELATIONSHIPS = {
  // Tags can be read by users with contacts permission
  [MODULE_PERMISSIONS.TAGS]: {
    readParents: [
      MODULE_PERMISSIONS.CONTACTS,
      MODULE_PERMISSIONS.DONATION_PLEDGE,
      MODULE_PERMISSIONS.DONATION_REMINDER,
      MODULE_PERMISSIONS.DONATION_TRANSACTION,
    ],
    writePermission: MODULE_PERMISSIONS.TAGS,
  },
  // Donation schemes can be read by users with any donation permission
  [MODULE_PERMISSIONS.DONATION_SCHEME]: {
    readParents: [
      MODULE_PERMISSIONS.DONATION_PLEDGE,
      MODULE_PERMISSIONS.DONATION_REMINDER,
      MODULE_PERMISSIONS.DONATION_TRANSACTION,
    ],
    writePermission: MODULE_PERMISSIONS.DONATION_SCHEME,
  },
  // Email templates can be read by users with email_marketing permission
  [MODULE_PERMISSIONS.EMAIL_TEMPLATE]: {
    readParents: [MODULE_PERMISSIONS.EMAIL_MARKETING],
    writePermission: MODULE_PERMISSIONS.EMAIL_TEMPLATE,
  },
  // WhatsApp templates can be read by users with whatsapp_marketing permission
  [MODULE_PERMISSIONS.WHATSAPP_TEMPLATE]: {
    readParents: [MODULE_PERMISSIONS.WHATSAPP_MARKETING],
    writePermission: MODULE_PERMISSIONS.WHATSAPP_TEMPLATE,
  },
  // Contacts can be read by users with any donation permission, email_marketing, or whatsapp_marketing permission
  [MODULE_PERMISSIONS.CONTACTS]: {
    readParents: [
      MODULE_PERMISSIONS.DONATION_PLEDGE,
      MODULE_PERMISSIONS.DONATION_REMINDER,
      MODULE_PERMISSIONS.DONATION_TRANSACTION,
      MODULE_PERMISSIONS.EMAIL_MARKETING,
      MODULE_PERMISSIONS.WHATSAPP_MARKETING,
    ],
    writePermission: MODULE_PERMISSIONS.CONTACTS,
  },
  // Trust master can be read by users with donation_pledge or donation_transaction permission
  [MODULE_PERMISSIONS.TRUST_MASTER]: {
    readParents: [MODULE_PERMISSIONS.DONATION_PLEDGE, MODULE_PERMISSIONS.DONATION_TRANSACTION],
    writePermission: MODULE_PERMISSIONS.TRUST_MASTER,
  },
};

/**
 * Module definitions with labels and descriptions
 */
export const MODULE_DEFINITIONS = [
  {
    key: MODULE_PERMISSIONS.CONTACTS,
    label: 'Contacts',
    description: 'Access to contact management, cannot delete contacts',
  },
  {
    key: MODULE_PERMISSIONS.EMAIL_MARKETING,
    label: 'Email Marketing',
    description: 'Access to email campaigns and email channel',
  },
  {
    key: MODULE_PERMISSIONS.WHATSAPP_MARKETING,
    label: 'WhatsApp Marketing',
    description: 'Access to WhatsApp campaigns and WhatsApp channel',
  },
  {
    key: MODULE_PERMISSIONS.DONATION_PLEDGE,
    label: 'Pledges',
    description: 'Access to pledge management',
  },
  {
    key: MODULE_PERMISSIONS.DONATION_REMINDER,
    label: 'Reminders',
    description: 'Access to reminder management',
  },
  {
    key: MODULE_PERMISSIONS.DONATION_TRANSACTION,
    label: 'Transactions',
    description: 'Access to transaction management',
  },
  {
    key: MODULE_PERMISSIONS.EMAIL_TEMPLATE,
    label: 'Email Templates',
    description: 'Access to email template management',
  },
  {
    key: MODULE_PERMISSIONS.WHATSAPP_TEMPLATE,
    label: 'WhatsApp Templates',
    description: 'Access to WhatsApp template management',
  },
  {
    key: MODULE_PERMISSIONS.TAGS,
    label: 'Tags',
    description: 'Access to tag management',
  },
  {
    key: MODULE_PERMISSIONS.DONATION_SCHEME,
    label: 'Donation Schemes',
    description: 'Access to donation scheme management',
  },
  {
    key: MODULE_PERMISSIONS.TRUST_MASTER,
    label: 'Trust Master',
    description: 'Access to trust master management',
  },
];

/**
 * Default permissions for each role
 */
export const DEFAULT_PERMISSIONS_BY_ROLE = {
  ADMIN: Object.values(MODULE_PERMISSIONS), // All permissions
  STAFF: [], // No default permissions, must be assigned
};

/**
 * Check if user has a specific module permission
 * @param {Object} user - User object with module_permissions array
 * @param {string} module - Module key to check
 * @returns {boolean}
 */
export const hasModulePermission = (user, module) => {
  if (!user) return false;

  // ADMIN always has all permissions
  if (user.role === 'ADMIN') return true;

  // Check module_permissions array
  const permissions = user.module_permissions || [];
  return permissions.includes(module);
};

/**
 * Check if user has any of the specified module permissions
 * @param {Object} user - User object with module_permissions array
 * @param {string[]} modules - Array of module keys to check
 * @returns {boolean}
 */
export const hasAnyModulePermission = (user, modules) => {
  if (!user) return false;
  if (user.role === 'ADMIN') return true;

  const permissions = user.module_permissions || [];
  return modules.some((module) => permissions.includes(module));
};

/**
 * Check if user has all of the specified module permissions
 * @param {Object} user - User object with module_permissions array
 * @param {string[]} modules - Array of module keys to check
 * @returns {boolean}
 */
export const hasAllModulePermissions = (user, modules) => {
  if (!user) return false;
  if (user.role === 'ADMIN') return true;

  const permissions = user.module_permissions || [];
  return modules.every((module) => permissions.includes(module));
};

/**
 * Check if user has write permission for a module
 * Write permission requires the specific module permission
 * @param {Object} user - User object with module_permissions array
 * @param {string} module - Module key to check write permission for
 * @returns {boolean}
 */
export const hasWritePermission = (user, module) => {
  if (!user) return false;
  if (user.role === 'ADMIN') return true;

  const permissions = user.module_permissions || [];

  // Check if there's a permission relationship defined
  const relationship = PERMISSION_RELATIONSHIPS[module];
  if (relationship) {
    return permissions.includes(relationship.writePermission);
  }

  // Default: check if user has the module permission directly
  return permissions.includes(module);
};

/**
 * Check if user has read permission for a module
 * Read permission can be granted by parent permissions or direct permission
 * @param {Object} user - User object with module_permissions array
 * @param {string} module - Module key to check read permission for
 * @returns {boolean}
 */
export const hasReadPermission = (user, module) => {
  if (!user) return false;
  if (user.role === 'ADMIN') return true;

  const permissions = user.module_permissions || [];

  // Check if user has direct permission
  if (permissions.includes(module)) return true;

  // Check if there's a permission relationship defined
  const relationship = PERMISSION_RELATIONSHIPS[module];
  if (relationship && relationship.readParents) {
    // Check if user has any of the parent permissions
    return relationship.readParents.some((parent) => permissions.includes(parent));
  }

  return false;
};

/**
 * Get permission status for a module (read-only, full, or none)
 * @param {Object} user - User object with module_permissions array
 * @param {string} module - Module key to check
 * @returns {Object} - { canRead: boolean, canWrite: boolean, isReadOnly: boolean }
 */
export const getPermissionStatus = (user, module) => {
  const canRead = hasReadPermission(user, module);
  const canWrite = hasWritePermission(user, module);

  return {
    canRead,
    canWrite,
    isReadOnly: canRead && !canWrite,
  };
};

/**
 * Get available communication channels based on user permissions
 * @param {Object} user - User object with module_permissions array
 * @returns {Object} - { canUseEmail: boolean, canUseWhatsApp: boolean }
 */
export const getAvailableChannels = (user) => {
  return {
    canUseEmail: hasModulePermission(user, MODULE_PERMISSIONS.EMAIL_MARKETING),
    canUseWhatsApp: hasModulePermission(user, MODULE_PERMISSIONS.WHATSAPP_MARKETING),
  };
};
